import { useAnalytics, type TrackOptions } from '@satellite/composables/useAnalytics';

import { buildCheckoutAnalyticsEventContext } from '@/analytics/checkoutAnalyticsEventContext';
import { Events, type CheckoutEventContextPayload, type JunoEventsMap } from '@/analytics/events';

import { useCheckoutFlow } from './useCheckoutFlow';
import { useWarehouseInfo } from './useWarehouseInfo';

/** Every Juno event carries the same payload, so one options type covers all of them. */
export type TrackCheckoutEventOptions = TrackOptions<CheckoutEventContextPayload>;

/**
 * Typed analytics for the carrier check-out flow — Juno's only domain.
 *
 * Replaces the untyped `useTrackJunoEvent(event: string)`: the event names now come from
 * `analytics/events.ts` and each step has its own `track*` function, so a typo is a compile
 * error rather than a stray event name in Mixpanel. Every function attaches the same
 * warehouse/org/appointment context the untyped version did.
 */
export function useCheckoutAnalytics() {
  const analytics = useAnalytics<JunoEventsMap>();
  const { warehouseData } = useWarehouseInfo();
  const { appointmentId, assetVisitId } = useCheckoutFlow();

  function buildPayload(options: TrackCheckoutEventOptions): CheckoutEventContextPayload {
    return {
      ...buildCheckoutAnalyticsEventContext({
        warehouse: warehouseData.value,
        appointmentId: appointmentId.value,
        assetVisitId: assetVisitId.value
      }),
      ...(options.additionalPayload ?? {})
    };
  }

  /** The carrier landed on the check-out app. */
  function trackCheckoutAppViewed(options: TrackCheckoutEventOptions = {}) {
    analytics.track(Events.VIEW.CHECKOUT_APP, buildPayload(options));
  }

  /** The confirmation number was validated and the asset visit resolved. */
  function trackValidationPageCompleted(options: TrackCheckoutEventOptions = {}) {
    analytics.track(Events.CHECKOUT.VALIDATION_PAGE, buildPayload(options));
  }

  /** The check-out custom forms were submitted. */
  function trackCheckoutDetailsPageCompleted(options: TrackCheckoutEventOptions = {}) {
    analytics.track(Events.CHECKOUT.CHECKOUT_DETAILS_PAGE, buildPayload(options));
  }

  /** The BOL was reviewed and signed. */
  function trackViewBolPageCompleted(options: TrackCheckoutEventOptions = {}) {
    analytics.track(Events.CHECKOUT.VIEW_BOL_PAGE, buildPayload(options));
  }

  /** The carrier dismissed the final screen, ending the flow. */
  function trackCheckoutCompletePageCompleted(options: TrackCheckoutEventOptions = {}) {
    analytics.track(Events.CHECKOUT.CHECKOUT_COMPLETE_PAGE, buildPayload(options));
  }

  return {
    trackCheckoutAppViewed,
    trackValidationPageCompleted,
    trackCheckoutDetailsPageCompleted,
    trackViewBolPageCompleted,
    trackCheckoutCompletePageCompleted
  };
}
